import React, { useState, useEffect } from "react";
import CodeMirror from "@uiw/react-codemirror";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardTitle,
  CardDescription,
} from "../ui/card";
import { Button } from "../ui/button";
import { PlayCircle, Download as DownloadIcon } from "lucide-react";
import { Input } from "../ui/input";

import { useToast, exportToCsv, useDatabase } from "@/lib";
import { useSqlAutocomplete } from "@/core/query/useSqlAutocomplete";
import { useQueryRunner } from "@/core/query/useQueryRunner";
import { downloadQueryToCsv } from "@/lib/duckdb/download";
import { getLimit, applyLimit } from "@/lib/utils/sql";
import DataTable from "@/core/data-tables/DataTable";
import { QueryErrorDisplay } from "../ui/error-display";
import { limitsConfig } from "@/config";

// NEW: shared hook & component
import { useDisplayOptions } from "@/core/data-tables/useDisplayOptions";
import { DisplayOptions } from "@/core/data-tables/DisplayOptions";

interface QueryEditorProps {
  setMsg: (msg: string) => void;
  initialQuery?: string;
  tabId?: string;
  onQueryChange?: (q: string) => void;
}

export default function QueryEditor({
  setMsg,
  initialQuery = "",
  onQueryChange,
}: QueryEditorProps) {
  const { conn } = useDatabase();
  const toast = useToast();

  const [sql, setSql] = useState(initialQuery);
  const extensions = useSqlAutocomplete(conn);

  const {
    data,
    cols,
    totalCount,
    rowCount,
    isPaginated,
    durationMs,
    error,
    run,
    page,
    next,
    prev,
  } = useQueryRunner(
    conn,
    limitsConfig.queryRunner.maxRows,
    limitsConfig.queryRunner.pageSize
  );

  const [csvName, setCsvName] = useState("");

  // 🎨 display options
  const {
    open: showOpts,
    toggle,
    improveNames,
    setImproveNames,
    scheme,
    setScheme,
  } = useDisplayOptions(false, "gray");

  useEffect(() => {
    setSql(initialQuery);
  }, [initialQuery]);

  const handleRun = async () => {
    setMsg("Running query…");
    await run(sql);
    setMsg(error ? `Error: ${error.message}` : "Query executed successfully");
    onQueryChange?.(sql);
  };

  const handleDownload = async () => {
    if (!conn || !sql.trim()) return;
    const userLimit = getLimit(sql) ?? Infinity;
    const effectiveDisplayLimit = Math.min(
      userLimit,
      limitsConfig.queryRunner.maxRows
    );
    const effectiveDownloadLimit = Math.min(
      userLimit,
      limitsConfig.csvDownload.maxRows
    );
  
    // Re-LIMIT the original SQL for download
    const limitedSql = applyLimit(sql, effectiveDownloadLimit);
    const file = `${csvName.trim() || "query_results"}.csv`;
    setMsg("Preparing CSV…");
    await downloadQueryToCsv(conn, limitedSql, file);
    setMsg(`CSV download: ${file}`);
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PlayCircle className="h-5 w-5 text-brand" /> SQL Query Editor
        </CardTitle>
        <CardDescription>
          Write and execute SQL queries against DuckDB
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-4">
        {/* Editor */}
        <div className="relative flex-1 rounded-md border overflow-hidden">
          <CodeMirror
            value={sql}
            onChange={(v) => {
              setSql(v);
              onQueryChange?.(v);
            }}
            extensions={extensions}
            placeholder="SELECT * FROM my_table LIMIT 100"
            theme="light"
            className="h-full"
          />
        </div>

        {/* Run / Options */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Results capped at{" "}
            {limitsConfig.queryRunner.maxRows.toLocaleString()} rows
          </span>

          <div className="flex items-center gap-2">
            <DisplayOptions
              open={showOpts}
              toggle={toggle}
              improveNames={improveNames}
              setImproveNames={setImproveNames}
              scheme={scheme}
              setScheme={setScheme}
            />

            <Button
              onClick={handleRun}
              disabled={!conn || !sql.trim()}
              variant="brand"
              className="flex items-center gap-2"
            >
              <PlayCircle className="h-4 w-4" /> Run
            </Button>
          </div>

          <span className="text-xs text-muted-foreground">
            {durationMs.toFixed(2)} ms
          </span>
        </div>

        {/* Results */}
        <div className="relative flex-1 overflow-hidden">
          {error ? (
            <QueryErrorDisplay
              error={error}
              query={sql}
              tableSchemas={[]}
              onCopy={() => {}}
              onDismiss={() => {}}
            />
          ) : data.length ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">
                  Showing {rowCount} of {totalCount.toLocaleString()} rows
                </span>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="file name"
                    value={csvName}
                    onChange={(e) => setCsvName(e.target.value)}
                    className="h-8 w-40"
                  />
                  <Button
                    onClick={handleDownload}
                    variant="outline"
                    size="sm"
                    disabled={data.length === 0}
                  >
                    <DownloadIcon className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>

              <DataTable
                data={data}
                columns={cols}
                maxHeight="600px"
                improveColumnNames={improveNames}
                colorScheme={scheme}
                enableRowCopy
              />

              {isPaginated && (
                <div className="flex items-center justify-center gap-4 mt-3">
                  <Button onClick={prev} size="sm" variant="outline" disabled={page === 0}>
                    Prev
                  </Button>
                  <span className="text-sm">
                    Page {page + 1} /{" "}
                    {Math.ceil(totalCount / limitsConfig.queryRunner.pageSize).toLocaleString()}
                  </span>
                  <Button
                    onClick={next}
                    size="sm"
                    variant="outline"
                    disabled={page >= Math.ceil(totalCount / limitsConfig.queryRunner.pageSize) - 1}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Enter a query and click “Run” to see results
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter />
    </Card>
);
}
